/**
 * The Advertising hub's URL vocabulary (PR 4; spec _overhaul-spec/pr04-hub.md
 * §0.1–0.3, B0): one route, `/ads?tab=<level>&<scope>&<labels>[&kind=][&neg=]`,
 * in place of six PPC pages that each opened their own bottom tab.
 *
 * Pure functions, no React (modelled on pages/automations/routing.ts), so
 * routing.test.ts can walk every rule and the hub's components stay thin.
 * Nothing here fetches or writes. The scope and label keys are exactly PR 3's
 * (lib/adsScope.ts); this module adds the level, the inner tab and the drill.
 *
 *   tab            strip label     body             scope honoured        inner tab
 *   campaigns      Campaigns       CampaignsTable   portfolio, campaign   —
 *   ad-groups      Ad groups       AdGroupsBody     campaign              —
 *   targets        Targets         TargetsBody      campaign, ad group,   kind=all|keywords|asins
 *                                                   product (asin)
 *   search-terms   Search terms    SearchTermsBody  campaign, ad group,   —
 *                                                   target
 *   placements     Placements      PlacementsBody   campaign              —
 *   negatives      Negatives       NegativesBody    campaign, ad group    neg=keywords|products|lists
 *
 * Navigation (each helper returns `{ to, replace }`, so a test can drive a
 * memory router with it):
 *   - a drill (a name cell, a row menu "Open…", a crumb) PUSHES: Back returns
 *     to the previous level and scope;
 *   - a level-strip or inner-tab click REPLACES and keeps the scope, so Back
 *     skips tab clicks and pops the last drill (owner decision 1).
 *
 * Links: every body builds its drill links through `AdsLinks`
 * (pages/ads/adsLinks.ts). Outside the hub that is `LEGACY_ADS_LINKS`, which
 * builds the same URLs the old pages always built; inside it is
 * `hubAdsLinks(scope, labels, appliedScopeFields(loc))`. PR 5 flipped the
 * default to the hub mapping (`DEFAULT_HUB_LINKS`) and redirects the old paths
 * (pages/ads/legacyRoutes.ts).
 */
import {
  pickScope, readAdsScope, scopeHref, writeAdsScope,
  type AdsScope, type AdsScopeField, type AdsScopeLabels,
} from '../../lib/adsScope'
import type { ScopeLabels } from '../../lib/api'

// ── Levels and inner tabs ─────────────────────────────────────────────────

export type AdsTab = 'campaigns' | 'ad-groups' | 'targets' | 'search-terms' | 'placements' | 'negatives'
export type TargetsKind = 'all' | 'keywords' | 'asins'
export type NegKind = 'keywords' | 'products' | 'lists'

export const ADS_PATH = '/ads'

/** The level strip, always visible directly above the table. */
export const ADS_TABS: ReadonlyArray<{ key: AdsTab; label: string }> = [
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'ad-groups', label: 'Ad groups' },
  { key: 'targets', label: 'Targets' },
  { key: 'search-terms', label: 'Search terms' },
  { key: 'placements', label: 'Placements' },
  { key: 'negatives', label: 'Negatives' },
]

export const TARGETS_KINDS: ReadonlyArray<{ key: TargetsKind; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'keywords', label: 'Keywords' },
  { key: 'asins', label: 'ASINs' },
]

export const NEG_KINDS: ReadonlyArray<{ key: NegKind; label: string }> = [
  { key: 'keywords', label: 'Negative keywords' },
  { key: 'products', label: 'Negative products' },
  { key: 'lists', label: 'Saved lists' },
]

const TAB_KEYS: ReadonlySet<string> = new Set(ADS_TABS.map(t => t.key))
const KIND_KEYS: ReadonlySet<string> = new Set(TARGETS_KINDS.map(t => t.key))
const NEG_KEYS: ReadonlySet<string> = new Set(NEG_KINDS.map(t => t.key))

/** The scope fields each level's list endpoint can narrow by — the same lists
 *  the old pages hand `useAdsScope`, so a body never shows a scope its table
 *  ignores. */
export const TAB_SCOPE_FIELDS: Record<AdsTab, readonly AdsScopeField[]> = {
  'campaigns': ['portfolioId', 'portfolioName', 'campaignId'],
  'ad-groups': ['campaignId'],
  'targets': ['campaignId', 'adGroupId', 'asin'],
  'search-terms': ['campaignId', 'adGroupId', 'targetId'],
  'placements': ['campaignId'],
  'negatives': ['campaignId', 'adGroupId'],
}

/** Where the hub is: the level, its inner tabs, and the scope with its names. */
export type AdsLocation = {
  tab: AdsTab
  kind: TargetsKind
  neg: NegKind
  scope: AdsScope
  labels: AdsScopeLabels
}

/** The fields the body on screen actually applies: the tab's list, and none
 *  on Saved lists (a saved list belongs to no campaign). */
export function appliedScopeFields(loc: Pick<AdsLocation, 'tab' | 'neg'>): readonly AdsScopeField[] {
  if (loc.tab === 'negatives' && loc.neg === 'lists') return []
  return TAB_SCOPE_FIELDS[loc.tab]
}

/** Read a hub URL. Unknown or missing `tab` → campaigns; `tab=keywords|asins`
 *  (the old Targets inner-tab values) → targets with that kind; an unknown
 *  `kind`/`neg` → the default. */
export function readAdsLocation(search: string): AdsLocation {
  const params = new URLSearchParams(search)
  const rawTab = params.get('tab')
  const rawKind = params.get('kind')
  const rawNeg = params.get('neg')
  let tab: AdsTab = rawTab && TAB_KEYS.has(rawTab) ? (rawTab as AdsTab) : 'campaigns'
  let kind: TargetsKind = rawKind && KIND_KEYS.has(rawKind) ? (rawKind as TargetsKind) : 'all'
  if (rawTab === 'keywords' || rawTab === 'asins') {
    tab = 'targets'
    kind = rawTab
  }
  const neg: NegKind = rawNeg && NEG_KEYS.has(rawNeg) ? (rawNeg as NegKind) : 'keywords'
  const { scope, labels } = readAdsScope(params)
  return { tab, kind, neg, scope, labels }
}

/** A fresh hub URL: `tab` + scope + labels (+ the inner tab when it is not the
 *  default). Never carries another level's column filters. */
export function adsHref(
  tab: AdsTab, scope: AdsScope, labels?: AdsScopeLabels,
  opts?: { kind?: TargetsKind; neg?: NegKind },
): string {
  const base = new URLSearchParams()
  base.set('tab', tab)
  const params = writeAdsScope(base, scope, labels)
  if (tab === 'targets' && opts?.kind && opts.kind !== 'all') params.set('kind', opts.kind)
  if (tab === 'negatives' && opts?.neg && opts.neg !== 'keywords') params.set('neg', opts.neg)
  return `${ADS_PATH}?${params.toString()}`
}

export type HubNav<R extends boolean> = { to: string; replace: R }

/** A level-strip click: REPLACE, keeping the scope, its labels and every other
 *  param (a level's column filter is still there when you come back); the
 *  inner tab is dropped unless the level stays the same. */
export function levelStripNav(currentSearch: string, tab: AdsTab): HubNav<true> {
  const params = new URLSearchParams(currentSearch)
  const rawTab = params.get('tab')
  const current = readAdsLocation(currentSearch)
  params.set('tab', tab)
  if (tab !== current.tab) {
    params.delete('kind')
    params.delete('neg')
  } else if (tab === 'targets' && (rawTab === 'keywords' || rawTab === 'asins') && !params.has('kind')) {
    params.set('kind', rawTab)
  }
  return { to: `${ADS_PATH}?${params.toString()}`, replace: true }
}

/** An inner-tab click (`kind` on Targets, `neg` on Negatives): REPLACE. The
 *  default value is written as no param at all. On an alias URL
 *  (`tab=keywords|asins`, which forces its own kind) the tab is first written
 *  as `targets`, or the click would change nothing. */
export function innerTabNav(currentSearch: string, key: 'kind' | 'neg', value: string): HubNav<true> {
  const params = new URLSearchParams(currentSearch)
  const rawTab = params.get('tab')
  if (key === 'kind' && (rawTab === 'keywords' || rawTab === 'asins')) params.set('tab', 'targets')
  const isDefault = key === 'kind' ? value === 'all' : value === 'keywords'
  if (isDefault) params.delete(key)
  else params.set(key, value)
  return { to: `${ADS_PATH}?${params.toString()}`, replace: true }
}

/** A drill: PUSH, so Back pops it. */
export function drillNav(href: string): HubNav<false> {
  return { to: href, replace: false }
}

// ── Drill scope ───────────────────────────────────────────────────────────

/** Every scope field: what a caller that names no applied list carries. */
const ALL_SCOPE_FIELDS: readonly AdsScopeField[] =
  ['portfolioId', 'portfolioName', 'campaignId', 'adGroupId', 'targetId', 'asin']

/** The scope a drill from inside the hub lands on: the row's own scope, plus
 *  the portfolio and the product carried over from where you are when the
 *  row does not set them — but ONLY when the rows on screen were narrowed by
 *  them. `applied` is the list the level on screen honours
 *  (`appliedScopeFields`). A field that only rides in the URL (dimmed in the
 *  breadcrumb) did not choose the row, so carrying it would claim a scope the
 *  row may sit outside: an ASIN left over from Targets, carried from Search
 *  terms into another campaign's ad group, empties its Targets table; a
 *  portfolio left over from Campaigns, carried from Placements into another
 *  portfolio's campaign, names the wrong portfolio in the trail and empties
 *  the Campaigns tab.
 *   - the product is carried when the level applies it;
 *   - the portfolio is carried when the level applies it, or when the level
 *     applies the campaign and the row stays in that campaign (the campaign
 *     lies inside the portfolio it was drilled from). */
export function mergeDrillScope(
  current: AdsScope, next: AdsScope, applied: readonly AdsScopeField[] = ALL_SCOPE_FIELDS,
): AdsScope {
  const out: AdsScope = {}
  for (const [k, v] of Object.entries(next) as [AdsScopeField, string | undefined][]) if (v) out[k] = v
  if (!next.portfolioId && !next.portfolioName && carriesPortfolio(current, next, applied)) {
    if (current.portfolioId) out.portfolioId = current.portfolioId
    else if (current.portfolioName) out.portfolioName = current.portfolioName
  }
  if (!next.asin && current.asin && applied.includes('asin')) out.asin = current.asin
  return out
}

function carriesPortfolio(current: AdsScope, next: AdsScope, applied: readonly AdsScopeField[]): boolean {
  if (applied.includes('portfolioId') || applied.includes('portfolioName')) return true
  return applied.includes('campaignId') && !!current.campaignId
    && (!next.campaignId || next.campaignId === current.campaignId)
}

/** The labels that ride with `mergeDrillScope`: the row's, plus the carried
 *  portfolio's name. */
function mergeDrillLabels(
  merged: AdsScope, next: AdsScope, currentLabels: AdsScopeLabels | undefined, labels: AdsScopeLabels | undefined,
): AdsScopeLabels {
  const out: AdsScopeLabels = { ...(labels ?? {}) }
  if (!next.portfolioId && merged.portfolioId && currentLabels?.portfolioName && !out.portfolioName) {
    out.portfolioName = currentLabels.portfolioName
  }
  return out
}

// ── Links (the contract every body builds through, §0.3) ──────────────────

export type AdsLinkEntity = 'campaign' | 'ad_group' | 'target'

export type AdsLinks = {
  inHub: boolean
  /** A literal level: "show <tab> for this scope". */
  level: (tab: AdsTab, scope: AdsScope, labels?: AdsScopeLabels, opts?: { kind?: TargetsKind; neg?: NegKind }) => string
  /** "Open this entity": the natural next level for it (§0.2). */
  open: (entity: AdsLinkEntity, scope: AdsScope, labels?: AdsScopeLabels) => string
}

/** The old page each level lives on today. */
export const LEGACY_PATH: Record<AdsTab, string> = {
  'campaigns': '/campaigns',
  'ad-groups': '/ad-groups',
  'targets': '/targets',
  'search-terms': '/search-terms',
  'placements': '/placements',
  'negatives': '/negatives',
}

/** The old pages' links carry ONE scope field — the narrowest the
 *  destination honours (a keyword's link names the keyword, not also its ad
 *  group and campaign) — so every URL an old page builds keeps the shape it
 *  always had. */
function narrowest(scope: AdsScope): AdsScope {
  if (scope.targetId) return { targetId: scope.targetId }
  if (scope.adGroupId) return { adGroupId: scope.adGroupId }
  if (scope.campaignId) return { campaignId: scope.campaignId }
  if (scope.portfolioId) return { portfolioId: scope.portfolioId }
  if (scope.portfolioName) return { portfolioName: scope.portfolioName }
  if (scope.asin) return { asin: scope.asin }
  return {}
}

/** Links outside the hub: today's URLs (LinkCells, AdGroups, Targets,
 *  Placements and Campaigns built these by hand until PR 4). */
export const LEGACY_ADS_LINKS: AdsLinks = {
  inHub: false,
  level(tab, scope, labels, opts) {
    const base = new URLSearchParams()
    if (tab === 'targets' && opts?.kind && opts.kind !== 'all') base.set('tab', opts.kind)
    if (tab === 'negatives' && opts?.neg) base.set('tab', opts.neg)
    const params = writeAdsScope(base, narrowest(pickScope(scope, TAB_SCOPE_FIELDS[tab])), labels)
    const qs = params.toString()
    return qs ? `${LEGACY_PATH[tab]}?${qs}` : LEGACY_PATH[tab]
  },
  open(entity, scope, labels) {
    // Outside the hub "open a campaign" filtered Campaigns to it (the
    // Campaign cells' link); an ad group opened its targets, a target its
    // search terms.
    switch (entity) {
      case 'campaign':
        return scopeHref('/campaigns', { campaignId: scope.campaignId }, { campaignName: labels?.campaignName })
      case 'ad_group':
        return scopeHref('/targets', { adGroupId: scope.adGroupId }, { adGroupName: labels?.adGroupName })
      case 'target':
        return scopeHref('/search-terms', { targetId: scope.targetId }, { targetLabel: labels?.targetLabel })
    }
  },
}

/** The level an entity opens on, inside the hub (owner decision 1). */
const OPEN_TAB: Record<AdsLinkEntity, AdsTab> = {
  campaign: 'ad-groups',   // the campaign workspace
  ad_group: 'targets',
  target: 'search-terms',
}

/** A "name" that is only its own id is no name: drop it, so the breadcrumb
 *  and the tab title fall back to /api/ads/scope-labels (names, never ids). */
function dropIdLabels(scope: AdsScope, labels: AdsScopeLabels): AdsScopeLabels {
  const out: AdsScopeLabels = { ...labels }
  if (out.portfolioName && out.portfolioName === scope.portfolioId) delete out.portfolioName
  if (out.campaignName && out.campaignName === scope.campaignId) delete out.campaignName
  if (out.adGroupName && out.adGroupName === scope.adGroupId) delete out.adGroupName
  if (out.targetLabel && out.targetLabel === scope.targetId) delete out.targetLabel
  return out
}

/** Links inside the hub, from where you are (`current`, the URL's scope) on a
 *  level that applies `applied` of it (`appliedScopeFields`; every field when
 *  omitted). Only applied fields are carried into a drill (`mergeDrillScope`). */
export function hubAdsLinks(
  current: AdsScope, currentLabels?: AdsScopeLabels, applied: readonly AdsScopeField[] = ALL_SCOPE_FIELDS,
): AdsLinks {
  const level: AdsLinks['level'] = (tab, scope, labels, opts) => {
    const merged = mergeDrillScope(current, scope, applied)
    return adsHref(tab, merged, dropIdLabels(merged, mergeDrillLabels(merged, scope, currentLabels, labels)), opts)
  }
  return {
    inHub: true,
    level,
    open: (entity, scope, labels) => level(OPEN_TAB[entity], scope, labels),
  }
}

/** The hub mapping from no scope: the default for code outside a hub body
 *  (PR 5), where there is no current scope to carry. */
export const DEFAULT_HUB_LINKS: AdsLinks = hubAdsLinks({})

/** "Open this thing" for code outside a hub body (Ctrl+K, Alerts, Product
 *  detail): a campaign opens its workspace, an ad group its Targets, a target
 *  its Search terms. */
export function adsOpenHref(entity: AdsLinkEntity, scope: AdsScope, labels?: AdsScopeLabels): string {
  return DEFAULT_HUB_LINKS.open(entity, scope, labels)
}

/** A row the drill table reads (§0.2). Nullable: rows carry nulls. */
export type DrillRow = {
  campaignId?: string | null
  campaignName?: string | null
  adGroupId?: string | null
  adGroupName?: string | null
  targetId?: string | null
  targetLabel?: string | null
}

const opt = (v: string | null | undefined): string | undefined => v || undefined

/** Where a row's name cell / "Open" goes (§0.2), or null when there is no
 *  lower level (a search term) or the row has no id to drill by. */
export function drillFor(
  level: 'campaign' | 'ad_group' | 'keyword' | 'target' | 'placement' | 'negative',
  row: DrillRow,
): { tab: AdsTab; scope: AdsScope; labels: AdsScopeLabels } | null {
  const campaign = { campaignId: opt(row.campaignId) }
  const campaignLabel = { campaignName: opt(row.campaignName) }
  switch (level) {
    case 'campaign':
    case 'placement':
      if (!row.campaignId) return null
      return { tab: 'ad-groups', scope: clean(campaign), labels: clean(campaignLabel) }
    case 'ad_group':
      if (!row.adGroupId) return null
      return {
        tab: 'targets',
        scope: clean({ ...campaign, adGroupId: opt(row.adGroupId) }),
        labels: clean({ ...campaignLabel, adGroupName: opt(row.adGroupName) }),
      }
    case 'keyword':
    case 'target':
      if (!row.targetId) return null
      return {
        tab: 'search-terms',
        scope: clean({ ...campaign, adGroupId: opt(row.adGroupId), targetId: opt(row.targetId) }),
        labels: clean({ ...campaignLabel, adGroupName: opt(row.adGroupName), targetLabel: opt(row.targetLabel) }),
      }
    case 'negative':
      if (row.adGroupId) return drillFor('ad_group', row)
      return drillFor('campaign', row)
  }
}

function clean<T extends Record<string, string | undefined>>(o: T): T {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(o)) if (v) out[k] = v
  return out as T
}

// ── Breadcrumb ────────────────────────────────────────────────────────────

export type CrumbField = 'all' | 'portfolio' | 'campaign' | 'adGroup' | 'target' | 'asin'

export type Crumb = {
  field: CrumbField
  label: string
  /** Where a click goes (push); null on the last crumb. */
  href: string | null
  /** False when the level on screen does not narrow by this field: the
   *  crumb is dimmed and `title` says why. */
  applied: boolean
  title?: string
}

/** The words for a name ClickPay does not have yet. A numeric id never
 *  appears as crumb text; it goes in the crumb's title only. */
export const UNSYNCED_LABEL: Record<'portfolio' | 'campaign' | 'adGroup' | 'target', string> = {
  portfolio: 'Portfolio (name not synced yet)',
  campaign: 'Campaign (name not synced yet)',
  adGroup: 'Ad group (name not synced yet)',
  target: 'Target (name not synced yet)',
}

/** Why a crumb is dimmed, one sentence per level and field. */
const NOT_APPLIED: Record<AdsTab, Partial<Record<CrumbField, string>>> = {
  'campaigns': {
    adGroup: 'Campaigns are listed whole, so the ad group isn\'t applied on this tab.',
    target: 'Campaigns are listed whole, so the target isn\'t applied on this tab.',
    asin: 'This list can\'t narrow campaigns by product, so the product isn\'t applied on this tab.',
  },
  'ad-groups': {
    portfolio: 'Ad groups are listed per campaign, so the portfolio isn\'t applied on this tab.',
    adGroup: 'Every ad group of the campaign is listed, so the ad group isn\'t applied on this tab.',
    target: 'Ad groups are listed whole, so the target isn\'t applied on this tab.',
    asin: 'This list can\'t narrow ad groups by product, so the product isn\'t applied on this tab.',
  },
  'targets': {
    portfolio: 'Targets are listed per campaign, so the portfolio isn\'t applied on this tab.',
    target: 'Every target of the ad group is listed, so the target isn\'t applied on this tab.',
  },
  'search-terms': {
    portfolio: 'Search terms are listed per campaign, so the portfolio isn\'t applied on this tab.',
    asin: 'Search terms can\'t be narrowed by product, so the product isn\'t applied on this tab.',
  },
  'placements': {
    portfolio: 'Placements are listed per campaign, so the portfolio isn\'t applied on this tab.',
    adGroup: 'Placements are per campaign, so the ad group isn\'t applied on this tab.',
    target: 'Placements are per campaign, so the target isn\'t applied on this tab.',
    asin: 'Placements are per campaign, so the product isn\'t applied on this tab.',
  },
  'negatives': {
    portfolio: 'Negatives are listed per campaign, so the portfolio isn\'t applied on this tab.',
    target: 'Negatives belong to a campaign or an ad group, so the target isn\'t applied on this tab.',
    asin: 'Negatives can\'t be narrowed by product, so the product isn\'t applied on this tab.',
  },
}

const CAMPAIGN_NOT_IN_URL = 'This campaign was found from the ad group or target, and this tab isn\'t narrowed by them, so it isn\'t applied here. Click it to open the campaign.'

const SAVED_LISTS_NOT_APPLIED = 'Saved lists belong to no campaign, so this isn\'t applied on this tab.'

const CRUMB_SCOPE_FIELD: Record<Exclude<CrumbField, 'all'>, AdsScopeField[]> = {
  portfolio: ['portfolioId', 'portfolioName'],
  campaign: ['campaignId'],
  adGroup: ['adGroupId'],
  target: ['targetId'],
  asin: ['asin'],
}

function notAppliedTitle(loc: AdsLocation, field: Exclude<CrumbField, 'all'>): string {
  if (loc.tab === 'negatives' && loc.neg === 'lists') return SAVED_LISTS_NOT_APPLIED
  return NOT_APPLIED[loc.tab][field] ?? 'This isn\'t applied on this tab.'
}

/**
 * The trail above the strip: "All campaigns › Portfolio › Campaign › Ad group
 * › Target", then "Product B0…" when a product is in scope. Names come from
 * the URL first, then from `resolved` (one /api/ads/scope-labels call), else a
 * plain placeholder. When the URL names an ad group or a target but no
 * campaign, the resolved parent campaign (and ad group) still shows — the URL
 * is not changed. Each crumb links to the level below it; the last one is not
 * a link.
 */
export function breadcrumb(loc: AdsLocation, resolved?: ScopeLabels | null): Crumb[] {
  const { scope, labels } = loc
  const applied = new Set(appliedScopeFields(loc))
  const crumbs: Crumb[] = []
  const trail: AdsScope = {}
  const trailLabels: AdsScopeLabels = {}
  if (scope.asin) trail.asin = scope.asin

  const push = (
    field: Exclude<CrumbField, 'all'>, label: string, href: string, idTitle?: string, notApplied?: string,
  ) => {
    const isApplied = !notApplied && CRUMB_SCOPE_FIELD[field].some(f => applied.has(f))
    const reasons = [isApplied ? null : (notApplied ?? notAppliedTitle(loc, field)), idTitle ?? null].filter(Boolean)
    crumbs.push({
      field, label, href, applied: isApplied,
      ...(reasons.length ? { title: reasons.join(' ') } : {}),
    })
  }

  crumbs.push({ field: 'all', label: 'All campaigns', href: adsHref('campaigns', {}), applied: true })

  if (scope.portfolioId || scope.portfolioName) {
    const name = labels.portfolioName ?? scope.portfolioName ?? resolved?.portfolio?.name ?? null
    if (scope.portfolioId) trail.portfolioId = scope.portfolioId
    else trail.portfolioName = scope.portfolioName
    if (scope.portfolioId && name) trailLabels.portfolioName = name
    push('portfolio', name ?? UNSYNCED_LABEL.portfolio, adsHref('campaigns', trail, trailLabels),
      name ? undefined : `Portfolio ID ${scope.portfolioId}`)
  }

  const campaignId = scope.campaignId ?? resolved?.ad_group?.campaign_id ?? resolved?.target?.campaign_id ?? undefined
  if (campaignId) {
    const name = labels.campaignName ?? resolved?.campaign?.name ?? resolved?.ad_group?.campaign_name
      ?? resolved?.target?.campaign_name ?? null
    trail.campaignId = campaignId
    if (name) trailLabels.campaignName = name
    // A campaign the URL does not name (found from its ad group or target)
    // narrows the rows only through that ad group or target: on a level that
    // does not apply them (Campaigns, Ad groups, Placements) it is not
    // applied, whatever the level could narrow by.
    const viaChild = !scope.campaignId
      && !((scope.adGroupId && applied.has('adGroupId')) || (scope.targetId && applied.has('targetId')))
    push('campaign', name ?? UNSYNCED_LABEL.campaign, adsHref('ad-groups', trail, trailLabels),
      name ? undefined : `Campaign ID ${campaignId}`,
      viaChild ? CAMPAIGN_NOT_IN_URL : undefined)
  }

  const adGroupId = scope.adGroupId ?? (scope.targetId ? resolved?.target?.ad_group_id ?? undefined : undefined)
  if (adGroupId) {
    const name = labels.adGroupName ?? resolved?.ad_group?.name ?? resolved?.target?.ad_group_name ?? null
    trail.adGroupId = adGroupId
    if (name) trailLabels.adGroupName = name
    push('adGroup', name ?? UNSYNCED_LABEL.adGroup, adsHref('targets', trail, trailLabels),
      name ? undefined : `Ad Group ID ${adGroupId}`)
  }

  if (scope.targetId) {
    const name = labels.targetLabel ?? resolved?.target?.label ?? null
    trail.targetId = scope.targetId
    if (name) trailLabels.targetLabel = name
    push('target', name ?? UNSYNCED_LABEL.target, adsHref('search-terms', trail, trailLabels),
      name ? undefined : `Target ID ${scope.targetId}`)
  }

  if (scope.asin) {
    // The ASIN is the product's own name for itself, not an internal id.
    push('asin', `Product ${scope.asin}`, adsHref('targets', trail, trailLabels))
  }

  crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], href: null }
  return crumbs
}

/** True when some scoped id has no name on the URL (or an ad group / target
 *  has no campaign): the hub then asks /api/ads/scope-labels, once. */
export function needsLabelLookup(loc: AdsLocation): boolean {
  const { scope, labels } = loc
  if (scope.portfolioId && !labels.portfolioName) return true
  if (scope.campaignId && !labels.campaignName) return true
  if (scope.adGroupId && !labels.adGroupName) return true
  if (scope.targetId && !labels.targetLabel) return true
  if ((scope.adGroupId || scope.targetId) && !scope.campaignId) return true
  return false
}

/** The /api/ads/scope-labels params for a location's ids. */
export function scopeLabelsParams(scope: AdsScope): {
  portfolio_id?: string; campaign_id?: string; ad_group_id?: string; target_id?: string
} {
  return clean({
    portfolio_id: scope.portfolioId,
    campaign_id: scope.campaignId,
    ad_group_id: scope.adGroupId,
    target_id: scope.targetId,
  })
}

// ── The bottom tab's title ────────────────────────────────────────────────

const TITLE_MAX = 40

function truncate(s: string): string {
  return s.length > TITLE_MAX ? `${s.slice(0, TITLE_MAX - 1)}…` : s
}

/** "Advertising · <the deepest name in scope>". An id with no name on the URL
 *  reads as its level ("Advertising · Ad group"), never as the id. */
export function adsTabTitle(search: string): string {
  const { scope, labels } = readAdsLocation(search)
  let what: string
  if (scope.targetId) what = labels.targetLabel ?? 'Target'
  else if (scope.adGroupId) what = labels.adGroupName ?? 'Ad group'
  else if (scope.campaignId) what = labels.campaignName ?? 'Campaign'
  else if (scope.portfolioId) what = labels.portfolioName ?? 'Portfolio'
  else if (scope.portfolioName) what = scope.portfolioName
  else if (scope.asin) what = `Product ${scope.asin}`
  else what = 'All campaigns'
  return `Advertising · ${truncate(what)}`
}
